import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { productsAPI, warehousesAPI, stockMovementsAPI } from '../../lib/api';
import { Package, AlertTriangle, Loader2, Plus, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const Stock = () => {
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '',
    warehouse_id: '',
    quantity: 0,
    movement_type: 'in',
    reference: '',
    notes: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, warehousesRes, movementsRes] = await Promise.all([
        productsAPI.getAll(),
        warehousesAPI.getAll(),
        stockMovementsAPI.getAll()
      ]);
      setProducts(productsRes.data);
      setWarehouses(warehousesRes.data);
      setMovements(movementsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const lowStockProducts = products.filter(p => p.quantity !== null && p.quantity !== undefined && p.quantity <= p.min_stock);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await stockMovementsAPI.create(formData);
      toast.success('Stock movement recorded');
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to record movement');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Stock Management</h1>
          <p className="text-slate-500 mt-1">Monitor inventory levels and movements</p>
        </div>
        <Button
          onClick={() => setIsDialogOpen(true)}
          className="bg-teal-700 hover:bg-teal-800"
          data-testid="add-stock-movement-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Record Movement
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-teal-50 rounded-lg">
                <Package className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Products</p>
                <p className="text-2xl font-bold text-slate-900">{products.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-50 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Low Stock Items</p>
                <p className="text-2xl font-bold text-slate-900">{lowStockProducts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Warehouses</p>
                <p className="text-2xl font-bold text-slate-900">{warehouses.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alert */}
      {lowStockProducts.length > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Low Stock Alert
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {lowStockProducts.map(product => (
                <div key={product.id} className="bg-white p-3 rounded-lg border border-amber-200">
                  <p className="font-medium text-sm text-slate-900">{product.name}</p>
                  <p className="text-xs text-slate-500">SKU: {product.sku}</p>
                  <p className="text-sm text-amber-600 font-medium mt-1">
                    Stock: {product.quantity} / Min: {product.min_stock}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stock Levels */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg">Current Stock Levels</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">SKU</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Product</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Category</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">In Stock</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Min Level</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map(product => (
                  <TableRow key={product.id} className="hover:bg-slate-50/50">
                    <TableCell className="text-sm font-mono text-slate-600">{product.sku || '-'}</TableCell>
                    <TableCell className="text-sm font-medium text-slate-900">{product.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{product.category || '-'}</TableCell>
                    <TableCell className="text-sm font-medium text-slate-900">{product.quantity === null || product.quantity === undefined ? 'Untracked' : `${product.quantity} ${product.unit}`}</TableCell>
                    <TableCell className="text-sm text-slate-600">{product.min_stock}</TableCell>
                    <TableCell>
                      {product.quantity === null || product.quantity === undefined ? (
                  <Badge className="bg-slate-100 text-slate-700">Untracked</Badge>
                ) : product.quantity <= product.min_stock ? (
                        <Badge className="bg-amber-100 text-amber-700">Low Stock</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700">In Stock</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Movements */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg">Recent Stock Movements</CardTitle>
        </CardHeader>
        <CardContent>
          {movements.length > 0 ? (
            <div className="space-y-3">
              {movements.slice(0, 10).map(movement => {
                const product = products.find(p => p.id === movement.product_id);
                return (
                  <div key={movement.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${movement.movement_type === 'in' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                        {movement.movement_type === 'in' ? (
                          <ArrowDownRight className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <ArrowUpRight className="h-4 w-4 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{movement.product_name || product?.name || 'Unknown Product'}</p>
                        <p className="text-xs text-slate-500">{movement.source_number || movement.reference || 'No reference'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${movement.movement_type === 'in' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {movement.delta !== null && movement.delta !== undefined ? (movement.delta > 0 ? `+${movement.delta}` : `${movement.delta}`) : `${movement.movement_type === 'in' ? '+' : '-'}${movement.quantity}`}
                      </p>
                      <p className="text-xs text-slate-500 capitalize">{movement.source_type || movement.movement_type}{movement.balance_after !== null && movement.balance_after !== undefined ? ` · Balance: ${movement.balance_after}` : ''}{movement.created_by ? ` · ${movement.created_by}` : ''}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">No stock movements recorded yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Movement Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Stock Movement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Product</Label>
                <Select
                  value={formData.product_id}
                  onValueChange={(val) => setFormData({...formData, product_id: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Warehouse</Label>
                <Select
                  value={formData.warehouse_id}
                  onValueChange={(val) => setFormData({...formData, warehouse_id: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Movement Type</Label>
                <Select
                  value={formData.movement_type}
                  onValueChange={(val) => setFormData({...formData, movement_type: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Stock In</SelectItem>
                    <SelectItem value="out">Stock Out</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="grid gap-2">
                <Label>Reference</Label>
                <Input
                  value={formData.reference}
                  onChange={(e) => setFormData({...formData, reference: e.target.value})}
                  placeholder="PO number, SO number, etc."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800">
                Record
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Stock;
